/**
 * Real (non-demo) self-serve signup.
 *
 * Every other account in this system is provisioned by `web/scripts/seed.ts`, running
 * with Admin credentials against the emulator. This is the one path where a stranger
 * with no prior Firestore document gets to become a user, which is why the shape here
 * is deliberately small: three roles only (`PATIENT`, `CLINICIAN`, `ORG_COORDINATOR`).
 * The staff-tier roles (`INVESTIGATOR`, `ORG_ANALYST`, `ORG_ADMIN`, `PLATFORM_ADMIN`)
 * stay seed-only, matching how the app already treats them as provisioned, not
 * self-served — see `docs/DECISIONS.md` for the write-up of why no identity
 * verification sits in front of this in the current build.
 */
import { z } from 'zod'
import { RoleSchema } from './vocab.js'
import { idSchema } from './zod-util.js'
import type { PatientId, UserId } from './ids.js'

/** Roles a stranger may pick for themselves at signup. A strict subset of `RoleSchema`. */
export const SelfServeRoleSchema = z.enum(['PATIENT', 'CLINICIAN', 'ORG_COORDINATOR'])
export type SelfServeRole = z.infer<typeof SelfServeRoleSchema>

export const SignUpInputSchema = z.object({
  email: z.string().email(),
  password: z.string().min(8, 'Password must be at least 8 characters.'),
  displayName: z.string().min(1),
  role: SelfServeRoleSchema,
  /** An existing seeded health system's org id. Ignored for `PATIENT`. */
  orgId: z.string().optional(),
  /** A brand-new organization's name, used when `orgId` is absent or unrecognized. */
  newOrgName: z.string().optional(),
})
export type SignUpInput = z.infer<typeof SignUpInputSchema>

export const SignUpResultSchema = z.object({
  uid: idSchema<UserId>('user id'),
  /** Present only when a `PATIENT` signup successfully claimed a synthetic record. */
  patientId: idSchema<PatientId>('patient id').optional(),
})
export type SignUpResult = z.infer<typeof SignUpResultSchema>

// Re-exported so callers of this module do not need a second import from `./vocab.js`
// just to describe the full role set a `SignUpInput.role` is a subset of.
export { RoleSchema }
